import { Controller } from "@hotwired/stimulus"

/**
 * Theme Controller - Dark Mode Toggle
 *
 * Utilise color-scheme + light-dark() CSS (2024+) pour basculer
 * entre modes clair et sombre. Pas de selecteurs [data-theme] ni
 * d'overrides !important necessaires pour les composants DS.
 *
 * Usage:
 * <div data-controller="theme">
 *   <button data-action="click->theme#toggle" data-theme-target="button">
 *     <span data-theme-target="iconLight">...</span>
 *     <span data-theme-target="iconDark">...</span>
 *   </button>
 * </div>
 *
 * La preference est sauvegardee dans localStorage.
 */
export default class extends Controller {
  static targets = ["button", "iconLight", "iconDark"]

  static values = {
    storageKey: { type: String, default: "dalms-theme" },
    defaultTheme: { type: String, default: "light" }
  }

  connect() {
    this.initializeTheme()
    this.updateUI()

    this.mediaQuery = window.matchMedia("(prefers-color-scheme: dark)")
    this.handleSystemChange = this.handleSystemChange.bind(this)
    this.mediaQuery.addEventListener("change", this.handleSystemChange)
  }

  disconnect() {
    if (this.mediaQuery) {
      this.mediaQuery.removeEventListener("change", this.handleSystemChange)
    }
  }

  initializeTheme() {
    const savedTheme = localStorage.getItem(this.storageKeyValue)
    const systemPrefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches

    if (savedTheme) {
      this.setTheme(savedTheme)
    } else if (systemPrefersDark) {
      this.setTheme("dark")
    } else {
      this.setTheme(this.defaultThemeValue)
    }
  }

  handleSystemChange(event) {
    if (!localStorage.getItem(this.storageKeyValue)) {
      this.setTheme(event.matches ? "dark" : "light")
      this.updateUI()
    }
  }

  toggle() {
    const currentTheme = this.getCurrentTheme()
    const newTheme = currentTheme === "dark" ? "light" : "dark"

    this.setTheme(newTheme)
    localStorage.setItem(this.storageKeyValue, newTheme)
    this.updateUI()

    this.announceThemeChange(newTheme)
  }

  getCurrentTheme() {
    return document.documentElement.getAttribute("data-theme") || "light"
  }

  setTheme(theme) {
    const root = document.documentElement

    // color-scheme pilote light-dark() dans les CSS variables
    root.style.colorScheme = theme

    // data-theme conserve pour les overrides structurels
    // (glass effects, gradients, SVG) qui ne sont pas de simples couleurs
    root.setAttribute("data-theme", theme)

    if (theme === "dark") {
      root.classList.add("dark")
    } else {
      root.classList.remove("dark")
    }
  }

  updateUI() {
    const isDark = this.getCurrentTheme() === "dark"

    this.iconLightTargets.forEach(el => el.classList.toggle("hidden", !isDark))
    this.iconDarkTargets.forEach(el => el.classList.toggle("hidden", isDark))

    this.buttonTargets.forEach(el => {
      el.setAttribute(
        "aria-label",
        isDark ? "Activer le mode clair" : "Activer le mode sombre"
      )
    })
  }

  announceThemeChange(theme) {
    const message = theme === "dark"
      ? "Mode sombre active"
      : "Mode clair active"

    const announcement = document.createElement("div")
    announcement.setAttribute("role", "status")
    announcement.setAttribute("aria-live", "polite")
    announcement.setAttribute("aria-atomic", "true")
    announcement.className = "sr-only"
    announcement.textContent = message

    document.body.appendChild(announcement)

    setTimeout(() => {
      announcement.remove()
    }, 1000)
  }

  setLight() {
    this.setTheme("light")
    localStorage.setItem(this.storageKeyValue, "light")
    this.updateUI()
  }

  setDark() {
    this.setTheme("dark")
    localStorage.setItem(this.storageKeyValue, "dark")
    this.updateUI()
  }

  setSystem() {
    localStorage.removeItem(this.storageKeyValue)
    const systemPrefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches
    this.setTheme(systemPrefersDark ? "dark" : "light")
    this.updateUI()
  }
}
